/**
 * Utilidad centralizada para clasificación de rechazo por porcentaje
 * Evita duplicación de lógica en múltiples componentes
 */

export const REJECTION_CLASSIFICATION = [
  {
    min: -Infinity,
    max: 1,
    label: "Excelente",
    statusIcon: "✓",
    range: "0% - 1%",
    colors: {
      bar: "bg-gradient-to-t from-green-400 to-green-500",
      text: "text-green-600",
      heatmap: "bg-green-100 text-green-700",
      badge: "bg-green-100 text-green-800",
      circle: "bg-gradient-to-br from-green-400 to-green-500"
    }
  },
  {
    min: 1,
    max: 2,
    label: "Bueno",
    statusIcon: "✓",
    range: "1% - 2%",
    colors: {
      bar: "bg-gradient-to-t from-blue-400 to-blue-500",
      text: "text-blue-600",
      heatmap: "bg-blue-100 text-blue-700",
      badge: "bg-blue-100 text-blue-800",
      circle: "bg-gradient-to-br from-blue-400 to-blue-500"
    }
  },
  {
    min: 2,
    max: 3,
    label: "Aceptable",
    statusIcon: "⚠",
    range: "2% - 3%",
    colors: {
      bar: "bg-gradient-to-t from-yellow-400 to-yellow-500",
      text: "text-yellow-600",
      heatmap: "bg-yellow-100 text-yellow-700",
      badge: "bg-yellow-100 text-yellow-800",
      circle: "bg-gradient-to-br from-yellow-400 to-yellow-500"
    }
  },
  {
    min: 3,
    max: 5,
    label: "Precaución",
    statusIcon: "⚠",
    range: "3% - 5%",
    colors: {
      bar: "bg-gradient-to-t from-orange-400 to-orange-500",
      text: "text-orange-600",
      heatmap: "bg-orange-100 text-orange-700",
      badge: "bg-orange-100 text-orange-800",
      circle: "bg-gradient-to-br from-orange-400 to-orange-500"
    }
  },
  {
    min: 5,
    max: Infinity,
    label: "Crítico",
    statusIcon: "✗",
    range: "> 5%",
    colors: {
      bar: "bg-gradient-to-t from-red-400 to-red-500",
      text: "text-red-600",
      heatmap: "bg-red-100 text-red-700",
      badge: "bg-red-100 text-red-800",
      circle: "bg-gradient-to-br from-red-400 to-red-500"
    }
  }
];

/**
 * Obtiene la clasificación para un porcentaje de rechazo
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {object} Objeto de clasificación con todos los datos
 */
export const getClassificationByPercentage = rejectionPercentage => {
  const value = parseFloat(rejectionPercentage) || 0;
  return (
    REJECTION_CLASSIFICATION.find(c => value > c.min && value <= c.max) ||
    REJECTION_CLASSIFICATION[REJECTION_CLASSIFICATION.length - 1]
  );
};

/**
 * Obtiene las clases Tailwind para barras de visualización
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Clases de gradiente
 */
export const getRejectionBarClass = rejectionPercentage => {
  return getClassificationByPercentage(rejectionPercentage).colors.bar;
};

/**
 * Obtiene las clases Tailwind para texto de métricas
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Clases de texto coloreado
 */
export const getRejectionClass = rejectionPercentage => {
  return getClassificationByPercentage(rejectionPercentage).colors.text;
};

/**
 * Obtiene las clases Tailwind para tabla de calor (heatmap)
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Clases para celdas de tabla
 */
export const getHeatMapClasses = rejectionPercentage => {
  return (
    getClassificationByPercentage(rejectionPercentage).colors.heatmap +
    " font-medium"
  );
};

/**
 * Obtiene las clases Tailwind para badges de estado
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Clases completas para badge
 */
export const getStatusBadgeClass = rejectionPercentage => {
  const classes =
    getClassificationByPercentage(rejectionPercentage).colors.badge;
  return `inline-block px-3 py-1 rounded-full text-xs font-semibold ${classes}`;
};

/**
 * Obtiene la etiqueta de estado con ícono
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Etiqueta con ícono (ej: "✓ Excelente")
 */
export const getStatusLabel = rejectionPercentage => {
  const classification = getClassificationByPercentage(rejectionPercentage);
  return `${classification.statusIcon} ${classification.label}`;
};

export default {
  REJECTION_CLASSIFICATION,
  getClassificationByPercentage,
  getRejectionBarClass,
  getRejectionClass,
  getHeatMapClasses,
  getStatusBadgeClass,
  getStatusLabel
};
